// ─── WorkRoamer Trust & Transparency Sprint — Terms Version Management ────────
//
// Single source of truth for current Terms of Service and Privacy Policy versions.
// Stored in OperatorPersona so users always know which version they accepted.
//
// Re-consent architecture: when CURRENT_TERMS_VERSION or CURRENT_PRIVACY_VERSION
// changes, Shell.tsx can call needsReconsent() and show a re-consent prompt.
// Minor wording fixes should NOT bump the version. Bump only for material changes.

export const CURRENT_TERMS_VERSION   = '1.0';
export const CURRENT_PRIVACY_VERSION = '1.0';
export const TERMS_EFFECTIVE_DATE    = 'July 24, 2026';
export const PRIVACY_EFFECTIVE_DATE  = 'July 24, 2026';

export interface TermsAcceptance {
  termsVersion:   string;
  privacyVersion: string;
  acceptedAt:     string; // ISO timestamp
}

const STORAGE_KEY = 'nomad:terms-acceptance:v1';

/** Record that the user has accepted the current terms at this moment. */
export function recordTermsAcceptance(): TermsAcceptance {
  const acceptance: TermsAcceptance = {
    termsVersion:   CURRENT_TERMS_VERSION,
    privacyVersion: CURRENT_PRIVACY_VERSION,
    acceptedAt:     new Date().toISOString(),
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(acceptance)); } catch { /* quota */ }
  return acceptance;
}

/** Load the stored acceptance record, or null if never accepted. */
export function loadTermsAcceptance(): TermsAcceptance | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TermsAcceptance;
  } catch { return null; }
}

/**
 * Returns true when the user needs to re-accept the terms.
 * Shell.tsx can call this on boot to gate access behind a re-consent screen.
 */
export function needsReconsent(): boolean {
  const stored = loadTermsAcceptance();
  if (!stored) return false; // new users handled by signup flow
  return (
    stored.termsVersion   !== CURRENT_TERMS_VERSION ||
    stored.privacyVersion !== CURRENT_PRIVACY_VERSION
  );
}

/** Human-readable date string for when the user accepted. */
export function formatAcceptedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
}
